/**
 * Beat Pulse Visualizer - Visualizes beat/onset detection with pulsing circle
 *
 * Shows a central circle that expands on beat detection, with:
 * - Ring showing onset strength
 * - Flash effect on isOnset
 * - Confidence arc indicator
 * - Optional beat history dots
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';

// Metadata for manifest generation
export const metadata = {
  name: 'beat-pulse',
  displayName: 'Beat Pulse',
  contextType: '2d',
};

class BeatPulse extends Base2DVisualizer {
  static name = 'beat-pulse';
  static displayName = 'Beat Pulse';

  /**
   * Get configuration UI definition
   */
  static getConfigUI() {
    return [
      { type: 'range', id: 'pulseScale', label: 'Pulse Scale', min: 10, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'decaySpeed', label: 'Decay Speed', min: 2, max: 20, default: 10, scale: 0.01 },
      { type: 'checkbox', id: 'showHistory', label: 'Show Beat History', default: true },
      { type: 'checkbox', id: 'showConfidence', label: 'Show Confidence Arc', default: true },
      { type: 'checkbox', id: 'flashOnBeat', label: 'Flash on Beat', default: true },
      { type: 'checkbox', id: 'glow', label: 'Glow Effect', default: true },
      { type: 'color', id: 'beatColor', label: 'Beat Color', default: '#ff0066' },
      { type: 'color', id: 'idleColor', label: 'Idle Color', default: '#333333' },
      { type: 'color', id: 'backgroundColor', label: 'Background', default: '#0a0a0a' }
    ];
  }

  /**
   * Visualizer-specific default configuration
   */
  getDefaultConfig() {
    return {
      pulseScale: 0.5,
      decaySpeed: 0.1,
      showHistory: true,
      showConfidence: true,
      flashOnBeat: true,
    };
  }

  /**
   * Initialize state
   */
  init() {
    this.pulseAmount = 0;
    this.beatHistory = [];
    this.maxHistory = 20;
    this.flashIntensity = 0;
    this.smoothedStrength = 0;
    this.ringRotation = 0;
  }

  /**
   * Returns the number of elements (1 main pulse)
   */
  getElementCount() {
    return 1;
  }

  /**
   * Interpolate between two hex colors
   */
  interpolateColor(color1, color2, t) {
    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);

    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);

    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);

    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Render a single frame
   */
  renderFrame(amplitudes, features) {
    if (!features) return;

    const { ctx, canvas, config } = this;
    const { isOnset, onsetStrength, confidence } = features.rhythm;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const baseRadius = Math.min(canvas.width, canvas.height) * 0.15;

    // Update pulse state on beat
    if (isOnset) {
      this.pulseAmount = 1.0;
      this.flashIntensity = 1.0;
      this.beatHistory.push({
        time: Date.now(),
        strength: onsetStrength,
        confidence: confidence
      });
      // Limit history
      if (this.beatHistory.length > this.maxHistory) {
        this.beatHistory.shift();
      }
    }

    // Decay pulse
    this.pulseAmount *= (1 - config.decaySpeed);
    this.flashIntensity *= 0.85;

    // Smooth onset strength for ring
    this.smoothedStrength = this.smoothedStrength * 0.9 + onsetStrength * 0.1;

    // Rotate ring slowly
    this.ringRotation += 0.01;

    // Draw flash effect
    if (config.flashOnBeat && this.flashIntensity > 0.01) {
      const flashGradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, canvas.width
      );
      flashGradient.addColorStop(0, `rgba(255, 255, 255, ${this.flashIntensity * 0.3})`);
      flashGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = flashGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // Draw beat history dots
    if (config.showHistory) {
      const now = Date.now();
      const historyRadius = baseRadius * 2.5;

      this.beatHistory.forEach((beat, index) => {
        const age = (now - beat.time) / 3000; // Fade over 3 seconds
        if (age < 1) {
          const alpha = 1 - age;
          const angle = (index / this.maxHistory) * Math.PI * 2 + this.ringRotation;
          const x = centerX + Math.cos(angle) * historyRadius;
          const y = centerY + Math.sin(angle) * historyRadius;
          const dotSize = 3 + beat.strength * 8;

          ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.6})`;
          ctx.beginPath();
          ctx.arc(x, y, dotSize, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    }

    // Draw confidence arc
    if (config.showConfidence) {
      const arcRadius = baseRadius * 1.8;
      const arcWidth = 8;

      // Background arc
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = arcWidth;
      ctx.beginPath();
      ctx.arc(centerX, centerY, arcRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Confidence arc
      const confAngle = confidence * Math.PI * 2;
      ctx.strokeStyle = config.beatColor;
      ctx.lineWidth = arcWidth;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(centerX, centerY, arcRadius, -Math.PI / 2, -Math.PI / 2 + confAngle);
      ctx.stroke();

      // Confidence label
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Confidence: ${(confidence * 100).toFixed(0)}%`, centerX, centerY + arcRadius + 25);
    }

    // Draw strength ring
    const ringRadius = baseRadius * 1.3;
    const ringThickness = 4 + this.smoothedStrength * 8;

    ctx.strokeStyle = this.interpolateColor(config.idleColor, config.beatColor, this.smoothedStrength);
    ctx.lineWidth = ringThickness;
    ctx.beginPath();
    ctx.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Draw main pulsing circle
    const pulseRadius = baseRadius * (1 + this.pulseAmount * config.pulseScale);
    const mainColor = this.interpolateColor(config.idleColor, config.beatColor, this.pulseAmount);

    // Glow effect
    if (config.glow) {
      ctx.shadowBlur = 30 + this.pulseAmount * 50;
      ctx.shadowColor = config.beatColor;
    }

    // Gradient fill - interpolate edge color from original hex colors
    const edgeColor = this.interpolateColor(config.idleColor, config.beatColor, this.pulseAmount * 0.5);
    const gradient = ctx.createRadialGradient(
      centerX, centerY, 0,
      centerX, centerY, pulseRadius
    );
    gradient.addColorStop(0, mainColor);
    gradient.addColorStop(1, edgeColor);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.shadowBlur = 0;

    // Draw center text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(isOnset ? 'BEAT!' : '', centerX, centerY);

    // Draw onset strength below
    ctx.font = '14px monospace';
    ctx.fillText(`Strength: ${onsetStrength.toFixed(3)}`, centerX, centerY + baseRadius * 2.8);
  }

  /**
   * Clear the canvas and reset state
   */
  clear() {
    super.clear();
    this.pulseAmount = 0;
    this.beatHistory = [];
    this.flashIntensity = 0;
    this.smoothedStrength = 0;
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(BeatPulse);

export { BeatPulse };
export default BeatPulse;
